import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { loadingTrue, loadingFalse } from "../../../Reducer/loaderSlice";
import Handler from "../../../Helpers/Handler";
import ReviewForm from "../Reviews/Reviewform";
import "../../../Assets/css/style.css";
import MainLayout from "../../../Partials/MainLayout";
import FeedLayout from "../../../Partials/FeedLayout";

const STATUS_COLORS = {
  PLANNING:  "badge-planning",
  ONGOING:   "badge-ongoing",
  COMPLETED: "badge-completed",
};

const VENDOR_STATUS = {
  INVITED:  "vs-invited",
  ACCEPTED: "vs-accepted",
  REJECTED: "vs-rejected",
};

const ViewEvent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [event,       setEvent]       = useState(null);
  const [vendors,     setVendors]     = useState([]);
  const [vendorPage,  setVendorPage]  = useState(1);
  const [vendorTotal, setVendorTotal] = useState(0);
  const [completing,  setCompleting]  = useState(false);

  // Review modal state
  const [reviewVendor, setReviewVendor] = useState(null);
  // reviewVendor = { id, name } of selected vendor

  const vendorLimit = 5;

  const fetchEventById = async () => {
    dispatch(loadingTrue());
    const res = await Handler({
      method: "GET",
      url: `/events/event_list.php?page=1&limit=100`,
    });
    dispatch(loadingFalse());
    if (res.success) {
      const found = (res.data?.list || []).find((e) => String(e.id) === String(id));
      if (found) setEvent(found);
      else toast.error("Event not found");
    }
  };

  const fetchVendors = async () => {
    const res = await Handler({
      method: "GET",
      url: `/events/event_vendor_list.php?event_id=${id}&page=${vendorPage}&limit=${vendorLimit}`,
    });
    if (res.success) {
      setVendors(res.data?.list || []);
      setVendorTotal(res.data?.pagination?.total || 0);
    }
  };

  useEffect(() => { fetchEventById(); }, [id]);
  useEffect(() => { fetchVendors();   }, [id, vendorPage]);

  const handleComplete = async () => {
    if (!window.confirm("Mark this event as completed?")) return;
    setCompleting(true);
    const res = await Handler({
      method: "POST",
      url: "/events/complete.php",
      data: { event_id: parseInt(id) },
    });
    setCompleting(false);
    if (res.success) {
      toast.success("Event marked as completed!");
      setEvent((e) => ({ ...e, status: "COMPLETED" }));
    } else {
      toast.error(res.message || "Failed to complete");
    }
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit", month: "long", year: "numeric",
    }) : "—";

  if (!event) {
    return (
      <MainLayout>
        <FeedLayout>
          <div className="ev-page">
            <div className="ev-empty">
              <i className="bx bx-loader-alt bx-spin ev-empty-icon" />
              <p>Loading event...</p>
            </div>
          </div>
        </FeedLayout>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <FeedLayout>
        <div className="ev-page">

          {/* Back */}
          <button className="ev-btn-back" onClick={() => navigate("/user/events")}>
            <i className="bx bx-arrow-back" /> Back to Events
          </button>

          {/* Event Hero */}
          <div className="ev-view-hero">
            <div className="ev-view-hero-left">
              <span className={`ev-badge large ${STATUS_COLORS[event.status] || "badge-planning"}`}>
                {event.status}
              </span>
              <h1 className="ev-view-title">{event.title}</h1>
              {event.description && (
                <p className="ev-view-desc">{event.description}</p>
              )}
            </div>
            <div className="ev-view-meta">
              <div className="ev-meta-card">
                <i className="bx bx-calendar ev-meta-icon" />
                <div>
                  <div className="ev-meta-label">Event Date</div>
                  <div className="ev-meta-value">{formatDate(event.event_date)}</div>
                </div>
              </div>
              <div className="ev-meta-card">
                <i className="bx bx-user ev-meta-icon" />
                <div>
                  <div className="ev-meta-label">Organizer</div>
                  <div className="ev-meta-value">{event.client_name}</div>
                </div>
              </div>
              <div className="ev-meta-card">
                <i className="bx bx-phone ev-meta-icon" />
                <div>
                  <div className="ev-meta-label">Contact</div>
                  <div className="ev-meta-value">{event.client_mobile || "—"}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="ev-actions-row">
            {event.status === "PLANNING" && (
              <button
                className="ev-btn-primary"
                onClick={() => navigate(`/user/link-community/${event.id}`)}
              >
                <i className="bx bx-link" /> Link Community
              </button>
            )}
            {event.status === "ONGOING" && (
              <>
                <button
                  className="ev-btn-primary"
                  onClick={() => navigate(`/user/invite/${event.id}`)}
                >
                  <i className="bx bx-user-plus" /> Invite Vendor
                </button>
                <button
                  className="ev-btn-success"
                  onClick={handleComplete}
                  disabled={completing}
                >
                  <i className="bx bx-check-double" />
                  {completing ? "Completing..." : "Mark Complete"}
                </button>
              </>
            )}
          </div>

          {/* Vendors Section */}
          <div className="ev-section">
            <div className="ev-section-header">
              <h2 className="ev-section-title">
                <i className="bx bx-group" /> Vendors ({vendorTotal})
              </h2>
              {/* Info hint when completed */}
              {event.status === "COMPLETED" && vendors.some(v => v.status === "ACCEPTED") && (
                <span className="ev-review-hint">
                  <i className="bx bxs-star" /> You can now rate accepted vendors
                </span>
              )}
            </div>

            {vendors.length === 0 ? (
              <div className="ev-vendor-empty">
                <i className="bx bx-user-x" />
                <p>No vendors linked yet</p>
              </div>
            ) : (
              <div className="ev-vendor-table-wrap">
                <table className="ev-vendor-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Service Type</th>
                      <th>Mobile</th>
                      <th>Status</th>
                      <th>Joined</th>
                      {/* Rate column — only show when event is COMPLETED */}
                      {event.status === "COMPLETED" && <th>Review</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {vendors.map((v, i) => (
                      <tr key={v.event_vendor_id}>
                        <td>{(vendorPage - 1) * vendorLimit + i + 1}</td>
                        <td>
                          <div className="ev-vendor-name">
                            <div className="ev-vendor-avatar">
                              {v.name?.charAt(0)?.toUpperCase()}
                            </div>
                            {v.name}
                          </div>
                        </td>
                        <td>{v.service_type || <span className="ev-na">—</span>}</td>
                        <td>{v.vendor_mobile || <span className="ev-na">—</span>}</td>
                        <td>
                          <span className={`ev-vendor-status ${VENDOR_STATUS[v.status]}`}>
                            {v.status}
                          </span>
                        </td>
                        <td>
                          {v.joined_at
                            ? new Date(v.joined_at).toLocaleDateString("en-IN")
                            : "—"}
                        </td>

                        {/* Rate Vendor button — only for COMPLETED events + ACCEPTED vendors */}
                        {event.status === "COMPLETED" && (
                          <td>
                            {v.status === "ACCEPTED" ? (
                              <button
                                className="ev-btn-rate"
                                onClick={() => setReviewVendor({
                                  id:   v.vendor_id,
                                  name: v.name,
                                })}
                              >
                                <i className="bx bxs-star" /> Rate
                              </button>
                            ) : (
                              <span className="ev-na">—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Vendor Pagination */}
            {vendorTotal > vendorLimit && (
              <div className="ev-vendor-pagination">
                <button
                  className="ev-btn-sm"
                  disabled={vendorPage <= 1}
                  onClick={() => setVendorPage((p) => p - 1)}
                >
                  <i className="bx bx-chevron-left" /> Prev
                </button>
                <span>
                  {vendorPage} / {Math.ceil(vendorTotal / vendorLimit)}
                </span>
                <button
                  className="ev-btn-sm"
                  disabled={vendorPage >= Math.ceil(vendorTotal / vendorLimit)}
                  onClick={() => setVendorPage((p) => p + 1)}
                >
                  Next <i className="bx bx-chevron-right" />
                </button>
              </div>
            )}
          </div>

        </div>

        {/* Review Modal — shown when a vendor is selected */}
        {reviewVendor && (
          <ReviewForm
            vendorId={reviewVendor.id}
            vendorName={reviewVendor.name}
            eventId={parseInt(id)}
            onClose={() => setReviewVendor(null)}
            onSuccess={() => {
              setReviewVendor(null);
              toast.success("Review submitted!");
            }}
          />
        )}

      </FeedLayout>
    </MainLayout>
  );
};

export default ViewEvent;